import { Button } from "@/components/ui/button";
import { useRouter } from "@tanstack/react-router";
import { AlertCircle, Home, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ErrorBoundaryProps {
  error: Error;
  reset: () => void;
}

export default function ErrorBoundary({ error, reset }: ErrorBoundaryProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const isDev = import.meta.env.DEV;

  return (
    <div className="h-full flex flex-col items-center justify-center gap-6 p-4 text-center">
      <div className="rounded-full bg-destructive/10 p-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
      </div>

      <div className="flex flex-col gap-2">
        <h1 className="font-serif text-3xl font-normal tracking-tight">
          {t("errors.boundary.title")}
        </h1>
        <p className="text-sm text-muted-foreground font-light max-w-sm">
          {t("errors.boundary.description")}
        </p>
      </div>

      {isDev && error && (
        <p className="text-xs font-mono text-muted-foreground bg-muted px-4 py-2 rounded-lg max-w-sm break-all">
          {error.message || error.toString()}
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <Button onClick={reset} className="rounded-full gap-2 px-6">
          <RefreshCw className="h-4 w-4" />
          {t("errors.boundary.tryAgain")}
        </Button>
        <Button
          variant="outline"
          onClick={() => router.navigate({ to: "/" })}
          className="rounded-full gap-2 px-6"
        >
          <Home className="h-4 w-4" />
          {t("errors.boundary.goHome")}
        </Button>
      </div>
    </div>
  );
}
